<template>
  <div
    class="app-main"
  >
    <modal-tagging-panel
      v-if="taggingPanelVisible"
      :credentialUUID="currentCredentialUUID"
    />
    <owal-add-identity
      v-if="showAddProfile"
      @close="handleEditClose"
      :identityToEdit="currentIdentityProfile"
      :initialImage="initialImage"
      />
    <div
      :tabindex="-1"
      @keydown.74="updateCurrentIndex(currentIdentityIndex - 1)"
      @keydown.75="updateCurrentIndex(currentIdentityIndex + 1)" style="width:100%; height:100%;"
      >
      <owal-panel
        :header-component="headerComponent"
        :header-props="headerProps"
        class="round-card"
        :show-top-banner="currentIdentityProfile !== undefined"
        @button-click="handleButton"
      >
        <div v-if="currentIdentityProfile">
          <div
            v-if="currentlyTagging.name"
            slot="content"
            class="owal-warning-banner"
            >
            {{ currentlyTagging.name.split(' ')[0] }} verified events for this identity less than {{ someoneElseThreshold }} seconds ago.
          </div>
          <div v-if="currentIdentityProfile">
            <credential-profile-toolbar
              :identityId="currentIdentityProfile.identityId"
              :cotenants="cotenants.length > 0"
              :credentials="currentIdentityProfile.credentials"
              :loadingReport="loadingReport"
              @filter-change="setFilterMode"
              @edit-profile="showAddProfile = true"
              @download-report="downloadReport"
              @credential-change="setCredentialIndex"
              />
            <div
              v-if="filterMode === 'match'"
              style="display: flex; width: 100%; height: 100%; flex-direction: row; flex-wrap: wrap; flex-shrink: 0; overflow-x: auto; max-witdh: 50%"
              >
              <div class="owal-keycard__unused-message">
                <div>
                  This keycard has
                  {{ verifiedMatchesForCurrentIdentity.length > 0 ?
                    verifiedMatchesForCurrentIdentity.filter(match => {
                      return match.rejected !== true
                    }).length : 'no' }}
                  verified matches.
                </div>
              </div>
              <div
                v-for="detection in confirmedTagsFromCache"
                :key="detection.detectionId"
                style="display: flex; flex-direction: row; flex-wrap: wrap;"
                >
                <access-event-detection-tile :detection="detection" />
              </div>
            </div>
            <div
              v-else-if="filterMode === 'mismatch'"
              style="display: flex; width: 100%; height: 100%; flex-direction: row; flex-wrap: wrap; flex-shrink: 0;"
            />
            <div v-else>
              <div v-for="accessEvent in eventsFilteredByView" :key="accessEvent.uuid" style="height:100%; width:100%">
                <access-event-card
                  :access-event="accessEvent"
                  :has-detections="accessEvent.detections.length > 0"
                />
              </div>
              <div
                v-if="limitAccessEventHistory"
                class="button"
                style="text-align: center; justify-content: center; margin-bottom: 4rem;"
                @click="getMoreAccessEvents()"
              >
                Load More
              </div>
            </div>
          </div>
          <div
            v-else
            class="owal-keycard__unused-message"
          >
            This keycard hasn't been used yet.
          </div>
        </div>
        <div v-if="!currentIdentityProfile">
          Please select a profile on the left hand side.
        </div>
      </owal-panel>
    </div>
  </div>
  <!-- close owal-panel -->
</template>

<script>
import { titleCase } from '@/libraries/util/string'
import OwalPanel from '@/libraries/owal-ui/panels/OwalPanel'
import AccessEventCard from '@/components/credential-mismatch-report/AccessEventCard'
import AccessEventDetectionTile from './AccessEventDetectionTile'
import ModalTaggingPanel from './event-tagging-panel/ModalTaggingPanel'
import FacesDetailsProfileHeader from '../faces/FacesDetailsProfileHeader'
import { mapGetters, mapActions, mapMutations } from 'vuex'
import { downloadAccessEventReport } from '@/libraries/api/credentialMismatch'
import { mapAndSortDetections } from '@/libraries/util/credentialMisuse'
import { imageForDetectionID, imageForIdentityId } from '@/libraries/api/faces'
import OwalAddIdentity from './AddIdentity'
import CredentialProfileToolbar from './CredentialProfileToolbar'

export default {
  name: 'CredentialMismatchPanel',
  components: {
    OwalPanel,
    AccessEventCard,
    AccessEventDetectionTile,
    ModalTaggingPanel,
    OwalAddIdentity,
    CredentialProfileToolbar,
  },
  data () {
    return {
      headerComponent: FacesDetailsProfileHeader,
      someoneElseThreshold: 60,
      filterMode: 'current',
      showRole: false,
      currentCredentialIndex: 0,
      loadingReport: {
        identity: false,
        unit: false,
      },
      showAddProfile: false,
      initialImage: undefined,
    }
  },
  computed: {
    ...mapGetters({
      identityIds: 'credentialMismatch/identityIds',
      accessEvents: 'credentialMismatch/sortedAccessEvents',
      taggingPanelVisible: 'credentialMismatch/taggingPanelVisible',
      selectedProfile: 'credentialMismatch/selectedProfile',
      filteredIdentities: 'credentialMismatch/filteredIdentities',
      currentIdentityIndex: 'credentialMismatch/currentIdentityIndex',
      verifiedMatchesForCurrentIdentity: 'credentialMismatch/verifiedMatchesForCurrentIdentity',
      limitAccessEventHistory: 'credentialMismatch/limitAccessEventHistory',
      currentDomainUUID: 'currentDomainUUID',
      cotenants: 'credentialMismatch/selectedIdentityCotenants',
    }),
    headerProps () {
      const profile = this.currentIdentityProfile

      if (!profile) return {}
      let tags = []

      const headerProps = {
        title: profile.person_name || profile.name,
        tags,
        currentIdentity: profile,
      }
      if (profile.matchRatio > 0) {
        tags.push(titleCase(`${this.detectionRatio(profile.matchRatio)}% of taps match`))
      } else {
        tags.push(titleCase(`0% of taps match`))
      }
      if (profile.unit) {
        let unitInfo = `Unit${' ' + profile.unit}`
        if (profile.role) {
          if (this.showRole) {
            unitInfo = `${unitInfo} (${profile.role})`
          } else {
            unitInfo = `${unitInfo} >`
          }
        }
        tags.push(unitInfo)
      }
      return headerProps
    },
    currentIdentityProfile () {
      // logic here needs to be ironed out.
      /**
       * should be: if there is an ID in the URL, use that, else use store, else
       */
      let identityId = this.$route.params.id || this.selectedProfile.identityId

      const filtered = this.identityIds.filter(profile => profile.identityId === identityId)
      return filtered[0] || undefined
    },
    eventsFilteredByView () {
      // clean source for sorting: https://forum.vuejs.org/t/do-not-mutate-vuex-store-state-outside-mutation-handlers/29631/3
      let accessEvents = JSON.parse(JSON.stringify(this.accessEvents))
      accessEvents.forEach(accessEvent => {
        if (accessEvent.detections) {
          accessEvent.detections = mapAndSortDetections(accessEvent.detections, accessEvent.confirmedDetections, this.currentIdentityProfile.identityId, !!accessEvent.bestMatchConfidenceLabel)
        }
      })
      let filtered = this.filterMode === 'all' ? accessEvents : accessEvents.filter(accessEvent => accessEvent.detections.length > 0)
      return filtered
    },
    confirmedTagsFromCache () {
      if (this.verifiedMatchesForCurrentIdentity.length > 0) {
        const verifiedTags = this.verifiedMatchesForCurrentIdentity.map(tag => {
          const imageUrl = imageForDetectionID(tag.detection_id)
          return {
            eventId: tag.access_event_uuid,
            detectionId: tag.detection_id,
            confirmedIds: tag.identity_id,
            rejected: tag.rejected,
            rejected_for: tag.rejected_for,
            imageUrl,
          }
        })
          .filter(tag => tag.rejected !== true)
        return verifiedTags
      } else return []
    },
    currentlyTagging () {
      const verifiedAccessEvents = this.accessEvents.filter(event => event.lastVerifiedTenantDescription && event.lastVerifiedTimestamp)

      let taggingInfo = {
        name: '',
        timestamp: '',
      }

      if (verifiedAccessEvents.length === 0 || !this.staff) {
        return taggingInfo
      }

      const accessEvent = verifiedAccessEvents.reduce((previousEvent, currentEvent) => {
        if (previousEvent.lastVerifiedTimestamp < currentEvent.lastVerifiedTimestamp) {
          return currentEvent
        }

        return previousEvent
      })
      if (accessEvent.lastVerifiedTenantDescription) {
        let verifyDate = new Date(accessEvent.lastVerifiedTimestamp).valueOf()
        let now = new Date().valueOf()

        let threshold = this.someoneElseThreshold

        let secondsAgo = (now - verifyDate) / 1000.0
        if (secondsAgo < threshold) {
          if (verifiedAccessEvents.length !== this.accessEvents.length) {
            taggingInfo.name = accessEvent.lastVerifiedTenantDescription
            taggingInfo.timestamp = accessEvent.lastVerifiedTimestamp
          }
        }
      }

      return taggingInfo
    },
    currentCredentialUUID () {
      return this.currentIdentityProfile.credentials[this.currentCredentialIndex].credential_uuid
    },
  },
  methods: {
    ...mapActions({
      loadVerifiedMatchesForIdentityId: 'credentialMismatch/loadVerifiedMatchesForIdentityId',
      loadDetectionsForCredentialUUID: 'credentialMismatch/loadDetectionsForCredentialUUID',
    }),
    ...mapMutations({
      setCurrentIdentityIndex: 'credentialMismatch/setCurrentIdentityIndex',
      setLimited: 'credentialMismatch/setLimited',
      setSelectedIdentityProfile: 'credentialMismatch/setSelectedIdentityProfile',
      updateCurrentIdentity: 'credentialMismatch/updateCurrentIdentity',
      addAccessEvents: 'credentialMismatch/addAccessEvents',
      updateIdentityProfile: 'credentialMismatch/updateIdentityProfile',
    }),
    setFilterMode (mode) {
      this.filterMode = mode
    },
    setCredentialIndex (index) {
      this.currentCredentialIndex = index
    },
    detectionRatio (ratio) {
      let percent = ratio * 100
      const output = Math.round(percent * 100) / 100
      return output
    },
    classFromConfidence (confidence) {
      return confidence > 0.95 ? 'confirmed' : 'misuse'
    },
    updateCurrentIndex (newIndex) {
      if (newIndex > this.filteredIdentities.length - 1) {
        newIndex = 0
      } else if (newIndex < 0) {
        newIndex = this.filteredIdentities.length - 1
      }
      this.$store.commit('credentialMismatch/setCurrentIdentityIndex', newIndex)
      this.$router.push(`/credential/${this.filteredIdentities[newIndex].identityId}`)
    },
    handleButton (identifier) {
      if (identifier === 'tag-button-unit') {
        this.showRole = !this.showRole
      }
    },
    getMoreAccessEvents () {
      this.$store.commit('credentialMismatch/setLimited', false)
      this.loadDetectionsForCredentialUUID(this.currentIdentityProfile.credentials[this.currentCredentialIndex].credential_uuid)
    },
    downloadReport (identifier) {
      this.loadingReport[identifier] = true
      let target = identifier === 'identity' ? this.currentIdentityProfile.identityId : this.currentIdentityProfile.unit
      downloadAccessEventReport(this.currentDomainUUID, target, 'pdf').then(() => {
        this.loadingReport[identifier] = false
      })
    },
    handleEditClose (event) {
      if (event.result) {
        const profileChanges = Object.assign({}, event.result, { name: `${event.result.first_name} ${event.result.last_name}`, role: event.result.info.occupancy_role })
        const updatedProfile = Object.assign({}, this.currentIdentityProfile, profileChanges)
        this.updateIdentityProfile({ index: this.currentIdentityIndex, updatedIdentityProfile: updatedProfile })
      }
      this.showAddProfile = false
    },
  },
  watch: {
    currentIdentityProfile: function (newIdentityProfile, oldIdentityProfile) {
      if (oldIdentityProfile === undefined || (newIdentityProfile.identityId !== oldIdentityProfile.identityId)) {
        const incomingIdentityIndex = this.filteredIdentities.findIndex(profile => {
          return profile.identityId === this.currentIdentityProfile.identityId
        })
        this.currentCredentialIndex = 0
        this.setCurrentIdentityIndex(incomingIdentityIndex)
        this.addAccessEvents([])
        this.loadVerifiedMatchesForIdentityId(this.currentIdentityProfile.identityId)
        this.setLimited(true)
        if (this.currentIdentityProfile.credentials) {
          this.loadDetectionsForCredentialUUID(this.currentIdentityProfile.credentials[this.currentCredentialIndex].credential_uuid)
        }
        this.setSelectedIdentityProfile(this.currentIdentityProfile)
        imageForIdentityId(this.currentIdentityProfile.identityId).then(response => {
          if (response.data) {
            let image = btoa(
              new Uint8Array(response.data).reduce((data, byte) => data + String.fromCharCode(byte), '')
            )
            this.initialImage = { data_b64: `data:'image/jpeg';base64,${image}`, i_b64: `data:'image/jpeg';base64,${image}` }
          }
        }).catch(error => {
          console.error(error)
          this.initialImage = undefined
        })
      }
    },
    filteredIdentities: function () {
      if (this.currentIdentityProfile) {
        const incomingIdentityIndex = this.filteredIdentities.findIndex(profile => {
          return profile.identityId === this.currentIdentityProfile.identityId
        })
        this.setCurrentIdentityIndex(incomingIdentityIndex)
      }
    },
    currentCredentialIndex: function () {
      this.setLimited(true)
      this.loadDetectionsForCredentialUUID(this.currentIdentityProfile.credentials[this.currentCredentialIndex].credential_uuid)
    },
  },
}
</script>

<style scoped>
@media only screen and (min-width: 768px) {
  .owal.modal.form.style .owal.panel.active.window,
  .owal.modal.form.style .owal.panel.inactive.window {
    max-width: 50rem;
    max-height: 80%;
  }
}

@media only screen and (max-width: 768px) {
}

.app-main {
  display: flex;
  height: 100%;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  align-content: center;
  margin: 0;
  padding: 0;
  overflow: auto;
}

.round-card {
  padding: 0;
  height: 100%;
  left: 0;
  right: 0;
  position: absolute;
}

.owal-element-header {
  flex-shrink: 0;
  background: #c0ccda;
  color: #475669;
  margin: -20px -20px 0 -20px;
  padding: 36px;
  min-height: 145px;
}

.owal-element-header h1 {
  margin: 0;
  margin-top: -16px;
  font-family: 'Lato-Bold', 'Helvetica Neue Bold', 'HelveticaNeue-Bold',
    'Helvetica';
  font-size: 48pt;
}

.owal-element-header .owal.faces.detail.profile {
  float: right;
  display: flex;
  flex-direction: row;
  height: 7.1428571429rem;
  width: 7.1428571429rem;
  padding: 0;
  margin: 0;
  justify-content: center;
}

*.icon {
  height: 13px;
  width: 13px;
  margin: 0 6px 0 5px;
  background-size: 100%;
  background-color: none;
  background-repeat: no-repeat;
  background-position: center;
  overflow: visible;
}

.owal.faces.list.avatar {
  /* yes */
  background-color: #eff2f7;
  object-fit: cover;
  height: 96px;
  width: 96px;
  border-radius: 24px;
  margin: 0.33rem 1.06rem 0.33rem 1rem;
}

#confirmed {
  border: 2px solid forestgreen;
}

#misuse {
  border: 2px solid yellow;
}

.owal-keycard__unused-message {
  display: flex;
  height: 100%;
  width: 100%;
  align-items: center;
  justify-content: center;
  font-family: 'Roboto', 'Avenir', Helvetica, Arial, sans-serif;
  font-weight: bold;
  font-size: 1.5rem;
  color: #8492a6;
  margin: 0.5rem;
}

.button {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  height: 2.0714285714rem;
  width: 100%;
  cursor: pointer;
  padding: 0;
  margin: 0 5px;
  font-size: 0.8571428571rem;
  font-family: 'Lato', 'Helvetica';
  border-radius: 10px;
  color: #9b9b9b;
}

.owal-warning-banner {
  width: fit-content;
  font-weight: bold;
  background-color: #f8e71c;
  color: #475669;
  border-radius: 0.35rem;
  padding: 0.35rem;
  margin: 0 auto;
}
</style>
